import React from "react"
import Link from "next/link"
import { Typography } from "antd"
import { css } from "@emotion/react"
import { MappedCourse } from "../../lib/prismic/MappedCourse"
import { CourseVariant } from "../../lib/prismic/Course"
import { CourseTechnology, getTechnology } from "../../lib/prismic/mapData"
import { PrismicCourseOrContentfulProduct } from "../../lib/contentful/PrismicCourseOrContentfulProduct"
import { getCourse } from "../../lib/prismic/getCourse"
import { AllProduct } from "../../lib/contentful/product/getAllProducts"
import { StyledParagraph } from "../StyledParagraph/StyledParagraph"

interface SimilarOrRelatedProductsProps {
  currentCourse: MappedCourse
  currentTechnology: CourseTechnology
  allCourses: AllProduct
}

export const getAvailableSampleCourses = (
  courses: AllProduct,
  currentCourse: MappedCourse,
  currentTechnology: CourseTechnology
): PrismicCourseOrContentfulProduct[] => {
  return (courses?.products?.items ?? []).filter((course) => {
    const t = getTechnology(course)
    const c = getCourse(course)

    return c.courseVariant === CourseVariant.sampleCourse && c.isOpen && t.slug === currentTechnology.slug && c.id !== currentCourse.id
  })
}

export const SimilarOrRelatedProducts: React.FC<SimilarOrRelatedProductsProps> = ({ currentCourse, currentTechnology, allCourses }) => {
  const { similarOrRelatedProducts, courseVariant } = currentCourse

  const isSampleCourse = courseVariant === CourseVariant.sampleCourse
  const sampleCoursesFromSimilar = similarOrRelatedProducts.filter((c) => c.courseVariant === CourseVariant.sampleCourse)
  const nonSampleCourses = similarOrRelatedProducts.filter((c) => c.courseVariant !== CourseVariant.sampleCourse)
  const fallbackSampleCourses = getAvailableSampleCourses(allCourses, currentCourse, currentTechnology)

  if (isSampleCourse) {
    return (
      <>
        <Typography.Title level={2}>Ukázkový lekce pro:</Typography.Title>

        {similarOrRelatedProducts.map((related) => (
          <StyledParagraph
            key={related.id}
            css={css`
              margin-top: 8px;
            `}>
            <Link href={related.registerUrl}>{related.name}</Link>
          </StyledParagraph>
        ))}
      </>
    )
  }

  if (sampleCoursesFromSimilar.length > 0) {
    return (
      <>
        <Typography.Title level={2}>Vyzkoušej si ukázkovou lekci zdarma</Typography.Title>

        {sampleCoursesFromSimilar.map((sample) => (
          <StyledParagraph
            key={sample.id}
            css={css`
              margin-top: 8px;
            `}>
            <Link href={sample.registerUrl}>{sample.name}</Link>
          </StyledParagraph>
        ))}
      </>
    )
  }

  if (fallbackSampleCourses.length > 0) {
    return (
      <>
        <Typography.Title level={2}>Vyzkoušejte si kroužek</Typography.Title>
        <StyledParagraph>
          Nejste si jistí, jestli je tento kroužek to pravé pro vás nebo vaše dítě? Nevadí! Přijďte si ho nejdřív zdarma vyzkoušet. Ukázková lekce vám pomůže
          zjistit, jak výuka probíhá, jestli vás téma baví a jestli vám sedí lektor i styl výuky. Vyberte si termín a dejte nám šanci vás nadchnout!
        </StyledParagraph>

        {fallbackSampleCourses.map((sample) => {
          const c = getCourse(sample)
          return (
            <StyledParagraph
              key={c.id}
              css={css`
                margin-top: 8px;
              `}>
              <Link target="_blank" href={c.registerUrl}>
                Ukázková lekce {c.whenAsString ? `– ${c.whenAsString}` : ""}
              </Link>
            </StyledParagraph>
          )
        })}
      </>
    )
  }

  if (nonSampleCourses.length > 0) {
    return (
      <>
        <Typography.Title level={2}>Související kroužky</Typography.Title>

        {nonSampleCourses.map((related) => (
          <StyledParagraph
            key={related.id}
            css={css`
              margin-top: 8px;
            `}>
            <Link href={related.registerUrl}>{related.name}</Link>
          </StyledParagraph>
        ))}
      </>
    )
  }

  return null
}
